import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './BottomNavigation.css';
import { useNavigation } from '../../contexts/NavigationContext';
import { useLanguage } from '../../contexts/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Shared BottomNavigation Component
 * Replaces duplicated bottom navigation bar across 7+ components
 *
 * @param {String} activeTab - Currently active tab ('home', 'messages', 'create', 'notifications', 'profile')
 * @param {Function} onNavigateToLanding - Navigate to home/landing page (optional, uses context if not provided)
 * @param {Function} onNavigateToProfile - Navigate to profile page (optional, uses context if not provided)
 * @param {Function} onNavigateToChatList - Navigate to chat list page (optional, uses context if not provided)
 * @param {Function} onCreateClick - Handle create button click (toggles create options)
 */
const BottomNavigation = ({
  activeTab = 'home',
  onNavigateToLanding,
  onNavigateToProfile,
  onNavigateToChatList,
  onNavigateToNotifications,
  onCreateClick
}) => {
  const navigation = useNavigation();
  const { t } = useLanguage();
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);

  // Fetch unread message and notification status
  useEffect(() => {
    const fetchUnreadStatus = async () => {
      try {
        // Fetch unread messages
        const messagesResponse = await axios.get(`${API_BASE_URL}/api/conversations`);
        const totalUnreadMessages = messagesResponse.data.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
        setHasUnreadMessages(totalUnreadMessages > 0);
      } catch (err) {
        // Silently fail - user might not be authenticated
      }

      try {
        // Issue #87: Fetch unread notifications count
        const notificationsResponse = await axios.get(`${API_BASE_URL}/api/notifications/unread-count`);
        setHasUnreadNotifications(notificationsResponse.data.unread_count > 0);
      } catch (err) {
        // Silently fail - user might not be authenticated
      }
    };

    fetchUnreadStatus();

    // Poll for updates every 5 seconds
    const pollInterval = setInterval(fetchUnreadStatus, 5000);

    return () => clearInterval(pollInterval);
  }, []);

  // Listen for notifications marked as read event from NotificationList
  useEffect(() => {
    const handleNotificationsMarkedRead = () => {
      setHasUnreadNotifications(false);
    };

    window.addEventListener('notificationsMarkedRead', handleNotificationsMarkedRead);
    return () => {
      window.removeEventListener('notificationsMarkedRead', handleNotificationsMarkedRead);
    };
  }, []);

  // Use props if provided, otherwise fall back to navigation context
  const handleNavigateToLanding = onNavigateToLanding || navigation.onNavigateToLandingPage;
  const handleNavigateToProfile = onNavigateToProfile || navigation.onNavigateToProfile;
  const handleNavigateToChatList = onNavigateToChatList || navigation.onNavigateToChatList;
  const handleNavigateToNotifications = onNavigateToNotifications || navigation.onNavigateToNotifications;

  return (
    <nav className="bottom-nav" aria-label={t('bottomNav.navigation') || 'Hauptnavigation'}>
      {/* Issue #115: Added aria-current for accessibility */}
      <button
        className={`nav-tab ${activeTab === 'home' ? 'active' : ''}`}
        onClick={handleNavigateToLanding}
        aria-current={activeTab === 'home' ? 'page' : undefined}
        aria-label={t('bottomNav.home') || 'Startseite'}
      >
        <span className="nav-icon" aria-hidden="true">&#127968;</span>
      </button>

      {/* Issue #91, #93: Messages tab with unread dot indicator and navigation */}
      <button
        className={`nav-tab ${activeTab === 'messages' ? 'active' : ''}`}
        onClick={handleNavigateToChatList}
        aria-current={activeTab === 'messages' ? 'page' : undefined}
        aria-label={`${t('bottomNav.messages') || 'Nachrichten'}${hasUnreadMessages ? ` (${t('bottomNav.unreadMessages') || 'ungelesen'})` : ''}`}
      >
        <span className="nav-icon-container">
          <span className="nav-icon" aria-hidden="true">&#128172;</span>
          {hasUnreadMessages && <span className="nav-unread-dot" aria-hidden="true"></span>}
        </span>
      </button>

      <button
        className={`nav-tab ${activeTab === 'create' ? 'active' : ''}`}
        onClick={onCreateClick}
        aria-current={activeTab === 'create' ? 'page' : undefined}
        aria-label={t('bottomNav.post') || 'Beitrag erstellen'}
        aria-haspopup="true"
      >
        <span className="nav-icon" aria-hidden="true">&#128221;</span>
      </button>

      {/* Issue #87: Notifications tab with unread dot indicator */}
      <button
        className={`nav-tab ${activeTab === 'notifications' ? 'active' : ''}`}
        onClick={handleNavigateToNotifications}
        aria-current={activeTab === 'notifications' ? 'page' : undefined}
        aria-label={`${t('bottomNav.notifications') || 'Benachrichtigungen'}${hasUnreadNotifications ? ` (${t('bottomNav.unreadNotifications') || 'ungelesen'})` : ''}`}
      >
        <span className="nav-icon-container">
          <span className="nav-icon" aria-hidden="true">&#128276;</span>
          {hasUnreadNotifications && <span className="nav-unread-dot" aria-hidden="true"></span>}
        </span>
      </button>

      <button
        className={`nav-tab ${activeTab === 'profile' ? 'active' : ''}`}
        onClick={handleNavigateToProfile}
        aria-current={activeTab === 'profile' ? 'page' : undefined}
        aria-label={t('bottomNav.profile') || 'Profil'}
      >
        <span className="nav-icon" aria-hidden="true">&#128100;</span>
      </button>
    </nav>
  );
};

export default BottomNavigation;
